import { ContactInfo } from './contactInfo';
import { Address } from './address';
import BasixBase from './basixBase';

// explicit values — must match the backend's persisted ints (item 43)
export enum ProposalStatus {
  Pending = 0,
  Approved = 1,
  Declined = 2,
  ApprovedDeposited = 3,
  Complete = 4,
}

// item 226 — RNWY sale type (must match the backend RnwySaleType ints)
export enum RnwySaleType {
  None = 0,
  Perpetual = 1,
  TwoYear = 2,
  FiveYear = 3,
}

// item 843 — external Customer Builder submission lifecycle (matches backend ints)
export enum CustomerSubmissionState {
  Building = 0, // customer still editing
  Submitted = 1, // submitted to Pulse, in review (locked to the customer)
}

export interface Proposal extends BasixBase {
  applyDiscountToTaxable: boolean;
  costProposalTotal: number;
  createdByName?: string;
  createdById: string;
  createdDate: string;
  customer: ContactInfo;
  customerId: string;
  customerName?: string; // present on index/search rows (the grid renders it)
  projectName?: string; // item 1587 — the index DTO's project name (Sales Bible PROJECT column)
  date: string;
  sentDate?: string | null; // item 837 — stamped when emailed to the client (grid SENT badge)
  // item 1142 — ENTERPRISE deploy (this deal only): finalize reminds the booker to zero
  // non-sales commissions; follow-up work on the same project comps normally
  isEnterprise?: boolean;
  // item 1180 — PAPER ONLY: minted for legacy contracts; out of opportunities/bible, stays live
  isContractOnly?: boolean;
  isCustomerSubmission?: boolean; // item 843 — built by an external Customer Builder
  customerSubmissionState?: CustomerSubmissionState; // item 843 — Building/Submitted
  discount: string;
  discountDescription: string;
  isChangeOrder: boolean;
  itemCategories: string;
  monthlyCategory?: string | null; // item 841 — the category billed monthly (null = no monthly section)
  itemizeCategory: boolean;
  itemizeEachLine: boolean;
  lastModifiedByName?: string;
  lastModifiedById: string;
  lastModifiedDate: string;
  paymentTerms: string;
  projectId: string;
  proposalItems: ProposalItem[];
  proposalNumber: string;
  proposalTitle: string;
  // item 465 — Service Address (end-user); name is proposalTitle, rest optional
  serviceAddress?: Address;
  servicePhone?: string;
  serviceEmail?: string;
  requestedBy: string;
  retailProposalTotal: number;
  // item 422 — the PROJECT page's embedded proposal rows (WebProposalIndexItemDto)
  // carry the total as retailTotal instead; consumers must accept either shape
  retailTotal?: number;
  taxState?: string; // item 179
  salesRepName: string;
  salesRepId: string;
  customerProjectManagerId?: string; // item 219 — the customer's PM (a CustomerStaff id)
  showCostColumn: boolean;
  // item 234 (reworked) — per-PDF price-column visibility (group price + MSRP/SRP suggestion)
  showGroupPriceOnPdf?: boolean;
  showMsrpOnPdf?: boolean;
  // item 710 (Chad) — AUTOMATED (RNWY) output visibility: which engine sections print on the PDF.
  // All default on. showHelpDeskOnPdf also gates the STANDARD help-desk offer block.
  showPerpetualOnPdf?: boolean;
  showTwoYearOnPdf?: boolean;
  showFiveYearOnPdf?: boolean;
  showHelpDeskOnPdf?: boolean;
  showDefinitionsOnPdf?: boolean; // item 1285 — gates the definitions block, both flows
  showSwitchPilotOnPdf?: boolean;
  showLinePricesOnPdf?: boolean; // item 1331 — per-line amounts above the One-time total (RNWY cards)
  statementOfWork: string;
  handOffNotes?: string | null; // item 423 — Hand-Off PDF only
  poNumber?: string | null; // item 611 — customer PO number (captured in the guided approval)
  depositPercent?: number | null; // item 433 — deposit split (null = 50/50 default)
  status: ProposalStatus;
  termsOption: number;
  termsText?: string | null;
  // item 177 (Sales Bible v2) — manual per-job tracking columns
  platform?: string | null;
  deploymentDate?: string | null;
  illustrationsComplete?: string | null;
  projectLeadPayoutInput?: string | null;
  salesBibleNotes?: string | null;
  // item 226 — RNWY contract flag + licensing inputs + License-Location sub-variable
  isRnwy?: boolean;
  rnwySaleType?: RnwySaleType;
  rnwyTwoYearAmount?: number | null;
  rnwyFiveYearAmount?: number | null;
  rnwyPerpetualAmount?: number | null;
  // item 706 (Chad) — a sold RNWY subscription (2yr/5yr) whose project has no live recurring
  // subscription yet → "Pending Recurring" (needs enrollment). Set by the index projection.
  pendingRecurring?: boolean;
  // item 788 (Chad) — approved RNWY deal with no prepared contract document yet
  pendingContracts?: boolean;
  internalProperty?: string | null;
  rnwyHelpDeskTier?: number; // item 241 — 0 None / 1 Gold / 2 Platinum / 3 Titanium
  // item 617 — the structured three-options pricing builder, serialized to JSON (see
  // shared/forms/rnwyPricing.ts). The backend rolls its computed totals into the three flat
  // amounts above on save, so this is the source of truth when the builder is used.
  rnwyPricingJson?: string | null;
  // RNWY 2.0 · P0/P2 — the fork template (0 Standard / 1 RNWY / 2 Crestron) + the venue model JSON
  kind?: number;
  rnwyVenuesJson?: string | null;
  rnwyManualPricingJson?: string | null; // item 954 — priced-externally manual snapshot
  // item 637 — this proposal's CFG rate-card snapshot (rates as they were when the quote was built)
  rnwyCfgJson?: string | null;
  // dart (2026-07-15, Chad) — STANDARD-quote Help Desk offer: JSON of the three tier prices
  // {"gold":..,"platinum":..,"titanium":..} to present (null/absent = Help Desk off). Perpetual +
  // non-RNWY jobs share these choices. The client picks one (or declines) at approval.
  helpDeskTierPricesJson?: string | null;
  // item 613 — the RNWY Standardized Language block (own section; pre-filled from the Settings
  // default). Distinct from statementOfWork (which stays for the real job scope). Prints on the PDF.
  rnwyStandardLanguage?: string | null;
  // item 616 — internal-use integrator / post-contract fields (never printed on the customer PDF).
  // The integrator PO reuses poNumber; the WO/job-code reuses the auto-created work order's number.
  integratorLegalName?: string | null;
  integratorCorporateAddress?: string | null;
  integratorSignatoryName?: string | null;
  integratorSignatoryTitle?: string | null;
  integratorSignatoryEmail?: string | null;
  // item 227 — client digital approval (a soft signal; the proposal stays Pending)
  clientApprovalToken?: string | null;
  clientApprovedDate?: string | null;
  clientApprovedByName?: string | null;
  // item 558 — internal approver (grid rows: the Basix user whose Approve/Process
  // moved the proposal to Approved; stamped once, never overwritten)
  approvedByName?: string | null;
  approvedDate?: string | null;
  // item 569 — assigned lead name(s) (index rows; biggest split first) for the Sales Bible
  projectLeadNames?: string[];
  // item 612 — the customer's PM / staff-contact NAME (index rows), resolved from
  // customerProjectManagerId, for the Sales Bible "Customer Staff / PM" column
  customerProjectManagerName?: string;
  // item 597 (REVERSES 570) — the per-proposal `platform` above IS the Sales Bible source
  // now; the per-project projectPlatform field is retired.
}

export interface ProposalItem {
  id: string;
  category: string;
  itemId: string;
  vendorId?: string;
  sku: string;
  brand: string;
  description: string;
  costEach: number;
  retailEach: number; // the GROUP price (binding) for this line
  listPrice?: number | null; // item 234 — MSRP/SRP suggestion (display only)
  isHardware?: boolean; // item 234 — MSRP (hardware) vs SRP (services) label
  quantity: number;
  taxable: boolean;
  includeInPo: boolean;
}

export interface ProposalsDto {
  index: Proposal[];
  length: number;
}
